import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';

import { Button } from 'components/Form/Button/Button';
import { Input } from 'components/Form/Input/Input/Input';

import { isValidQuantity } from '../../helpers/quantityValidation';
import { LineItemIdentifiers } from '../LineItemIdentifiers/LineItemIdentifiers';

export type DropQuantityRow = {
  key: string;
  productName: string;
  identifiers: Array<string>;
  unitOfMeasure?: string;
  max: number;
  // For placed items being moved: the source transport unit label (e.g. "Unit 1.2"), so the user
  // sees which unit each quantity is taken from when a selection spans several units.
  sourceLabel?: string;
};

type DropQuantityDialogProps = {
  title: string;
  rows: Array<DropQuantityRow>;
  confirmLabel?: string;
  onConfirm: (quantities: Record<string, number>) => void;
  onCancel: () => void;
};

/**
 * i18n keys
 * t('webedi:packing_list.placement_dialog.available', { available })
 * t('webedi:packing_list.placement_dialog.available_with_unit', { available, unit })
 * t('webedi:packing_list.drop_dialog.from_unit', { unit })
 * t('webedi:packing_list.placement_dialog.error.invalid_quantity', { available })
 */
export const DropQuantityDialog = ({ title, rows, confirmLabel, onConfirm, onCancel }: DropQuantityDialogProps) => {
  const { t } = useTranslation();
  const [quantities, setQuantities] = useState<Record<string, number>>(() =>
    Object.fromEntries(rows.map((row) => [row.key, row.max])),
  );
  // Whether to show a row's error at all — its own validity (below) decides whether there is one
  // to show, so this only needs to flip on once, at the first confirm attempt.
  const [showErrors, setShowErrors] = useState(false);

  // Typing directly (including clearing the field to retype a new value) must not be fought by
  // clamping on every keystroke, same as the single-item dialog — validation happens once, on
  // confirm, below.
  const updateQuantity = (key: string, value: number) => {
    setQuantities((prev) => ({ ...prev, [key]: value }));
  };

  // The +/- steppers move a known-good value by one, so clamping them immediately (rather than on
  // confirm) is the expected, discoverable behavior — unlike free typing, there is no in-between
  // state to preserve.
  const stepQuantity = (key: string, next: number, max: number) => updateQuantity(key, clamp(next, max));

  const handleConfirm = () => {
    if (rows.some((row) => !isValidQuantity(quantities[row.key], row.max))) {
      setShowErrors(true);
      return;
    }
    onConfirm(quantities);
  };

  return (
    <Dialog open onClose={onCancel} className="relative z-60">
      <DialogBackdrop className="fixed inset-0 bg-gray-500/75" />
      <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
        <DialogPanel className="flex max-h-[80vh] w-full max-w-md flex-col rounded-lg bg-white p-5 shadow-xl">
          <p className="text-sm font-semibold text-gray-900">{title}</p>
          <div className="mt-4 flex-1 space-y-4 overflow-y-auto">
            {rows.map((row) => {
              const availableLabel = row.unitOfMeasure
                ? t('webedi:packing_list.placement_dialog.available_with_unit', {
                    available: row.max,
                    unit: row.unitOfMeasure,
                  })
                : t('webedi:packing_list.placement_dialog.available', { available: row.max });
              const hasError = showErrors && !isValidQuantity(quantities[row.key], row.max);
              return (
                <div key={row.key}>
                  <p className="text-sm text-gray-900">{row.productName}</p>
                  {row.sourceLabel ? (
                    <p className="text-xs text-gray-500">
                      {t('webedi:packing_list.drop_dialog.from_unit', { unit: row.sourceLabel })}
                    </p>
                  ) : null}
                  <LineItemIdentifiers
                    parts={[...row.identifiers, availableLabel]}
                    className="mt-1 text-xs text-gray-500"
                  />
                  <div className="mt-2 flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="base"
                      iconOnly
                      LeftIcon={MinusIcon}
                      aria-label={t('webedi:packing_list.placement_dialog.decrease')}
                      onClick={() => stepQuantity(row.key, quantities[row.key] - 1, row.max)}
                      analyticsId="webedi:packing_list_drop_decrease"
                    >
                      {t('webedi:packing_list.placement_dialog.decrease')}
                    </Button>
                    <Input
                      type="number"
                      aria-label={t('webedi:packing_list.placement_dialog.quantity_label')}
                      value={quantities[row.key]}
                      fieldState={hasError ? 'error' : 'default'}
                      className="text-center"
                      wrapperClassName="min-w-0 flex-1"
                      onChange={(event) => updateQuantity(row.key, Number(event.target.value))}
                      rightAddon={
                        row.unitOfMeasure ? (
                          <span className="shrink-0 text-sm text-gray-500">{row.unitOfMeasure}</span>
                        ) : null
                      }
                    />
                    <Button
                      variant="secondary"
                      size="base"
                      iconOnly
                      LeftIcon={PlusIcon}
                      aria-label={t('webedi:packing_list.placement_dialog.increase')}
                      onClick={() => stepQuantity(row.key, quantities[row.key] + 1, row.max)}
                      analyticsId="webedi:packing_list_drop_increase"
                    >
                      {t('webedi:packing_list.placement_dialog.increase')}
                    </Button>
                  </div>
                  {hasError && (
                    <p className="mt-2 text-sm text-red-600">
                      {t('webedi:packing_list.placement_dialog.error.invalid_quantity', { available: row.max })}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" onClick={onCancel} analyticsId="webedi:packing_list_drop_cancel">
              {t('common:cancel')}
            </Button>
            <Button onClick={handleConfirm} analyticsId="webedi:packing_list_drop_confirm">
              {confirmLabel ?? t('webedi:packing_list.drop_dialog.add')}
            </Button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

const clamp = (value: number, max: number): number => {
  if (!Number.isFinite(value)) {
    return 1;
  }
  return Math.min(Math.max(1, Math.round(value)), max);
};
